import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import { CopyIcon, AddIcon } from '@chakra-ui/icons'
import { IoMdSave } from 'react-icons/io'
import {
  Box,
  Button,
  Text,
  Stack,
  Input,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  useToast,
  HStack,
  VStack,
  IconButton,
  CloseButton,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Wrap,
  WrapItem,
  Badge,
  Divider,
  Select
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { useEffect, useMemo, useState } from 'react'
import SizeCalculator from '../components/SizeCalculator'
import ApiManager from '../components/ApiManager'
import { updateTrading } from '../services/apiService'
import { useAuthState } from '../utils/authUtils'

type RoiTrigger = {
  roi: string
  size: string
}

const Trading = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { reloadUser } = useAuthState()

  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose
  } = useDisclosure()

  const {
    isOpen: isBatchOpen,
    onOpen: onBatchOpen,
    onClose: onBatchClose
  } = useDisclosure()

  const [confirmAction, setConfirmAction] = useState<'start' | 'stop' | null>(
    null
  )

  // ✅ 심볼 선택
  const availableSymbols = useMemo(
    () =>
      Array.isArray(auth?.trading) ? auth.trading.map((t) => t.symbol) : [],
    [auth?.trading]
  )

  const defaultSymbol = useMemo(() => {
    if (!availableSymbols.length) return 'XRP'
    // XRP 우선, 없으면 첫 번째
    return availableSymbols.includes('XRP') ? 'XRP' : availableSymbols[0]
  }, [availableSymbols])

  const [selectedSymbol, setSelectedSymbol] = useState<string>(defaultSymbol)

  // ✅ 현재 심볼의 트레이딩 설정 불러오기
  const currentTrading = useMemo(() => {
    if (!Array.isArray(auth?.trading)) return undefined
    return auth.trading.find((t) => t.symbol === selectedSymbol)
  }, [auth?.trading, selectedSymbol])

  // ✅ 폼 상태
  const [leverage, setLeverage] = useState('10')
  const [takeProfit, setTakeProfit] = useState('10')
  const [stopLoss, setStopLoss] = useState('0')
  const [size, setSize] = useState('1')
  const [roiTriggers, setRoiTriggers] = useState<RoiTrigger[]>([])
  const [useStopLoss, setUseStopLoss] = useState(false)
  const [direction, setDirection] = useState(0) // (0: 롱숏, 1: only 롱, 2: only 숏)

  const [isRunning, setIsRunning] = useState(false)

  // ✅ 심볼별 사이즈 단위 매핑
  const symbolSizeMap = {
    BTC: '0.0001 BTC',
    ETH: '0.01 ETH',
    SOL: '1 SOL',
    DOGE: '10 DOGE',
    XRP: '10 XRP'
  }

  // ✅ 현재 심볼의 사이즈 단위
  const currentSizeUnit =
    symbolSizeMap[selectedSymbol as keyof typeof symbolSizeMap] || '10 XRP'

  // ✅ 물타기 현황 계산
  const addOrderStatus = useMemo(() => {
    if (!currentTrading) return null
    const current = currentTrading.currentAddOrder || 0
    const total = currentTrading.addOrder || 0
    return { current, total }
  }, [currentTrading])

  // ✅ 배치 입력
  const [batchCount, setBatchCount] = useState('100')
  const [batchRoi, setBatchRoi] = useState('30')
  const [batchSize, setBatchSize] = useState('1')

  // ✅ 심볼 또는 유저 데이터 바뀔 때 폼 동기화
  useEffect(() => {
    if (!currentTrading) {
      // 심볼 설정이 아직 없는 경우 기본값 세팅
      setLeverage('10')
      setTakeProfit('10')
      setStopLoss('0')
      setSize('1')
      setUseStopLoss(false)
      setIsRunning(false)
      setDirection(0)
      setRoiTriggers([])
      return
    }

    setLeverage(String(currentTrading.leverage ?? 10))
    setTakeProfit(String(currentTrading.tp ?? 10))
    setStopLoss(String(currentTrading.sl ?? 0))
    setSize(String(currentTrading.size ?? 1))
    setUseStopLoss(Boolean(currentTrading.sl !== null && currentTrading.sl > 0))
    setIsRunning(Boolean(currentTrading.botStatus))
    setDirection(currentTrading.direction ?? 0)

    if (
      Array.isArray(currentTrading.dynamic) &&
      currentTrading.dynamic.length > 0
    ) {
      const roiList = currentTrading.dynamic.map((item) => ({
        roi: String(item.roi),
        size: String(item.size)
      }))
      setRoiTriggers(roiList)
    } else {
      setRoiTriggers([]) // 물타기 없음
    }
  }, [currentTrading])

  // ✅ 심볼 목록 변동 시 선택값 보호
  useEffect(() => {
    if (!availableSymbols.includes(selectedSymbol)) {
      setSelectedSymbol(defaultSymbol)
    }
  }, [availableSymbols, defaultSymbol, selectedSymbol])

  // ===== Handlers =====
  const handleSave = async () => {
    if (!selectedSymbol) {
      toast({
        title: t('심볼 선택 필요'),
        description: t('설정을 저장할 종목을 먼저 선택하세요.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    try {
      const { data: res } = await updateTrading({
        symbol: selectedSymbol, // ✅ 심볼 전달
        botStatus: null,
        leverage: Number(leverage),
        takeProfit: Number(takeProfit),
        stopLoss: Number(stopLoss),
        size: Number(size),
        direction,
        roiTriggers: roiTriggers.map((item) => ({
          roi: Number(item.roi),
          size: Number(item.size)
        })),
        useStopLoss: useStopLoss
      })
      if (res.statusCode === 200) {
        toast({
          title: t('자동매매 설정'),
          description: res.message,
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('자동매매 설정'),
        description:
          err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  const handleToggleAutoTrade = async () => {
    if (!selectedSymbol) {
      toast({
        title: t('심볼 선택 필요'),
        description: t('봇 시작/종료할 종목을 먼저 선택하세요.'),
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }

    if (isRunning) {
      // 종료 로직
      try {
        const { data: res } = await updateTrading({
          symbol: selectedSymbol, // ✅ 심볼 전달
          botStatus: false
        })
        if (res.statusCode === 200) {
          toast({
            title: t('{{symbol}} 봇 종료', { symbol: selectedSymbol }),
            description: res.message,
            status: 'success',
            duration: 2000,
            isClosable: true
          })
          reloadUser()
        }
      } catch (err: any) {
        toast({
          title: t('{{symbol}} 봇 종료', { symbol: selectedSymbol }),
          description:
            err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    } else {
      // 시작 로직
      try {
        const { data: res } = await updateTrading({
          symbol: selectedSymbol, // ✅ 심볼 전달
          botStatus: true,
          leverage: Number(leverage),
          takeProfit: Number(takeProfit),
          stopLoss: Number(stopLoss),
          size: Number(size),
          direction,
          roiTriggers: roiTriggers.map((item) => ({
            roi: Number(item.roi),
            size: Number(item.size)
          })),
          useStopLoss: useStopLoss
        })
        if (res.statusCode === 200) {
          toast({
            title: t('{{symbol}} 봇 시작', { symbol: selectedSymbol }),
            description: res.message,
            status: 'success',
            duration: 2000,
            isClosable: true
          })
          reloadUser()
        }
      } catch (err: any) {
        toast({
          title: t('{{symbol}} 봇 시작', { symbol: selectedSymbol }),
          description:
            err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }

    setIsRunning((prev) => !prev)
  }

  const handleBatchApply = () => {
    const count = Math.max(1, Number(batchCount))
    const roi = Math.max(1, Number(batchRoi))
    const size = Math.max(1, Number(batchSize))

    const newTriggers = Array.from({ length: count }, () => ({
      roi: String(roi),
      size: String(size)
    }))

    setRoiTriggers(newTriggers)
    onBatchClose()
  }

  const addRoiTrigger = () => {
    const last = roiTriggers[roiTriggers.length - 1] || { roi: '30', size: '1' }
    setRoiTriggers([...roiTriggers, { roi: last.roi, size: last.size }])
  }

  const removeRoiTrigger = (index: number) => {
    const updated = [...roiTriggers]
    updated.splice(index, 1)
    setRoiTriggers(updated)
  }

  // ===== UI =====
  return (
    <DefaultLayout>
      <Box maxW="3xl" mx="auto" p={6}>
        {/* 심볼 선택 섹션 */}
        <Box mb={4}>
          <HStack justify="space-between" align="center" mb={2}>
            <Text fontSize="xl" fontWeight="bold">
              {t('선물 자동매매')}
            </Text>

            {/* 현재 선택된 종목 상태 배지 */}
            <HStack>
              <Badge colorScheme="purple" variant="subtle">
                {t('선택 종목: {{symbol}}', {
                  symbol: selectedSymbol || '-'
                })}
              </Badge>
              <Badge colorScheme={isRunning ? 'green' : 'gray'} variant="solid">
                {t(isRunning ? 'RUNNING' : 'STOPPED')}
              </Badge>
              {addOrderStatus && addOrderStatus.total > 0 && (
                <Badge colorScheme="blue" variant="outline">
                  {t('물타기 ({{current}}/{{total}})', {
                    current: addOrderStatus.current,
                    total: addOrderStatus.total
                  })}
                </Badge>
              )}
            </HStack>
          </HStack>

          {/* 심볼 빠른 선택 버튼 + Select 조합 */}
          <Wrap spacing={2} mb={2}>
            {availableSymbols.map((sym) => (
              <WrapItem key={sym}>
                <Button
                  size="sm"
                  variant={selectedSymbol === sym ? 'solid' : 'outline'}
                  colorScheme={selectedSymbol === sym ? 'purple' : 'gray'}
                  onClick={() => setSelectedSymbol(sym)}
                >
                  {sym}
                </Button>
              </WrapItem>
            ))}
          </Wrap>

          <HStack>
            <Select
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              maxW="200px"
            >
              {availableSymbols.map((sym) => (
                <option key={sym} value={sym}>
                  {sym}
                </option>
              ))}
            </Select>

            {auth.id && (
              <Button
                colorScheme={isRunning ? 'red' : 'green'}
                flex="1"
                onClick={() => {
                  setConfirmAction(isRunning ? 'stop' : 'start')
                  onConfirmOpen()
                }}
              >
                {isRunning ? t('자동매매 진행중') : t('자동매매 시작')}
              </Button>
            )}
          </HStack>

          <Divider mt={4} />
        </Box>

        {/* API Key / SizeCalculator 섹션 */}
        <Box mb={2}>
          <HStack spacing={4} align="start">
            {auth.id && <ApiManager />}
            <SizeCalculator />
          </HStack>
        </Box>

        {/* Trading Settings Section */}
        <Box mt={6}>
          <HStack justify="space-between" align="center" mb={4}>
            <Text fontSize="xl" fontWeight="bold">
              {t('{{symbol}} 설정', { symbol: selectedSymbol })}
            </Text>

            {auth.id && (
              <HStack spacing={2}>
                <Button
                  colorScheme="purple"
                  size="sm"
                  leftIcon={<IoMdSave />}
                  onClick={handleSave}
                >
                  {t('설정 저장')}
                </Button>
              </HStack>
            )}
          </HStack>

          {/* 방향 설정 */}
          <Box mb={4}>
            <Text mb={2} fontWeight="semibold">
              {t('시그널 방향 설정')}
            </Text>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              spacing={2}
              flexWrap="wrap"
            >
              <Button
                w="100%"
                variant={direction === 0 ? 'solid' : 'outline'}
                colorScheme="purple"
                onClick={() => setDirection(0)}
              >
                {t('롱숏 시그널 둘다 허용')}
              </Button>
              <Button
                w="100%"
                variant={direction === 1 ? 'solid' : 'outline'}
                colorScheme="green"
                onClick={() => setDirection(1)}
              >
                {t('롱 시그널만 허용')}
              </Button>
              <Button
                w="100%"
                variant={direction === 2 ? 'solid' : 'outline'}
                colorScheme="red"
                onClick={() => setDirection(2)}
              >
                {t('숏 시그널만 허용')}
              </Button>
            </Stack>
          </Box>

          {/* 레버리지 */}
          <Box mb={4}>
            <Text mb={2}>{t('레버리지: {{n}}배', { n: leverage })}</Text>

            {/* 퀵 버튼 */}
            <HStack mb={2} flexWrap="wrap">
              {[1, 5, 10, 20, 30, 40, 50, 60, 75].map((val) => (
                <Button
                  key={val}
                  size="xs"
                  colorScheme={leverage === String(val) ? 'purple' : 'gray'}
                  variant={leverage === String(val) ? 'solid' : 'outline'}
                  onClick={() => setLeverage(String(val))}
                >
                  {val}x
                </Button>
              ))}
            </HStack>

            <HStack>
              <Slider
                value={Number(leverage)}
                onChange={(val) => setLeverage(String(val))}
                min={1}
                max={75}
                step={1}
                flex="1"
              >
                <SliderTrack>
                  <SliderFilledTrack bg="purple.500" />
                </SliderTrack>
                <SliderThumb />
              </Slider>

              <Input
                w="70px"
                type="number"
                min={1}
                max={75}
                value={leverage}
                onChange={(e) => setLeverage(e.target.value)}
                onBlur={() => {
                  if (!leverage || Number(leverage) < 1) setLeverage('1')
                }}
              />
            </HStack>
          </Box>

          {/* 익절 */}
          <Box mb={4}>
            <Text mb={1}>{t('익절 (%)')}</Text>

            {/* 퀵 버튼 */}
            <HStack mb={2} flexWrap="wrap">
              <Button
                size="xs"
                onClick={() =>
                  setTakeProfit((prev) => {
                    const next = Math.max(1, Number(prev) - 1)
                    return String(next)
                  })
                }
              >
                -1
              </Button>

              {[5, 6, 7, 8, 9, 10].map((val) => (
                <Button
                  key={val}
                  size="xs"
                  variant={takeProfit === String(val) ? 'solid' : 'outline'}
                  colorScheme={takeProfit === String(val) ? 'purple' : 'gray'}
                  onClick={() => setTakeProfit(String(val))}
                >
                  {val}
                </Button>
              ))}
              <Button
                size="xs"
                onClick={() => setTakeProfit(String(Number(takeProfit) + 1))}
              >
                +1
              </Button>
            </HStack>

            <Input
              type="number"
              min={1}
              value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)}
              onBlur={() => {
                if (!takeProfit || Number(takeProfit) < 1) setTakeProfit('1')
              }}
            />
          </Box>

          {/* 손절 */}
          <Box mb={4}>
            <HStack justify="space-between" mb={1}>
              <Text>{t('손절 (%)')}</Text>
              <HStack spacing={2}>
                <Text fontSize="sm">{t('손절 기능 사용')}</Text>
                <Switch
                  isChecked={useStopLoss}
                  onChange={(e) => {
                    const isChecked = e.target.checked
                    setUseStopLoss(isChecked)
                    if (!isChecked) setStopLoss('0')
                  }}
                  colorScheme="purple"
                />
              </HStack>
            </HStack>

            {/* 퀵 버튼 */}
            <HStack
              mb={2}
              flexWrap="wrap"
              opacity={useStopLoss ? 1 : 0.4}
              pointerEvents={useStopLoss ? 'auto' : 'none'}
            >
              <Button
                size="xs"
                onClick={() =>
                  setStopLoss((prev) => {
                    const next = Math.max(1, Number(prev) - 1)
                    return String(next)
                  })
                }
              >
                -1
              </Button>

              {[5, 6, 7, 8, 9, 10].map((val) => (
                <Button
                  key={val}
                  size="xs"
                  variant={stopLoss === String(val) ? 'solid' : 'outline'}
                  colorScheme={stopLoss === String(val) ? 'purple' : 'gray'}
                  onClick={() => setStopLoss(String(val))}
                >
                  {val}
                </Button>
              ))}
              <Button
                size="xs"
                onClick={() => setStopLoss(String(Number(stopLoss) + 1))}
              >
                +1
              </Button>
            </HStack>

            <Input
              type="number"
              min={1}
              value={stopLoss}
              onChange={(e) => setStopLoss(e.target.value)}
              onBlur={() => {
                if (!stopLoss || Number(stopLoss) < 1) setStopLoss('1')
              }}
              isDisabled={!useStopLoss}
              placeholder={t('손절 비율 입력') as string}
            />
          </Box>

          {/* 사이즈 */}
          <Box mb={4}>
            <Text mb={1}>
              {t('사이즈 (1 Size = {{unit}})', { unit: currentSizeUnit })}
            </Text>
            <HStack>
              <Input
                type="number"
                min={1}
                value={size}
                onChange={(e) => setSize(e.target.value)}
                onBlur={() => {
                  if (!size || Number(size) < 1) setSize('1')
                }}
              />
              <IconButton
                aria-label="paste"
                icon={<CopyIcon />}
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText()
                    const parsed = parseFloat(text)
                    if (!isNaN(parsed)) {
                      setSize(String(parsed))
                      toast({
                        title: t('사이즈 입력'),
                        description: t('"{{value}}" 값이 입력되었습니다.', {
                          value: parsed
                        }),
                        status: 'success',
                        duration: 2000,
                        isClosable: true
                      })
                    } else {
                      toast({
                        title: t('유효하지 않은 숫자입니다.'),
                        description: t('붙여넣은 값이 숫자가 아닙니다.'),
                        status: 'error',
                        duration: 2000,
                        isClosable: true
                      })
                    }
                  } catch (err) {
                    toast({
                      title: t('붙여넣기 실패'),
                      description: t('클립보드에서 값을 읽을 수 없습니다.'),
                      status: 'error',
                      duration: 2000,
                      isClosable: true
                    })
                  }
                }}
                size="sm"
                variant="ghost"
                colorScheme="purple"
              />
            </HStack>
          </Box>

          {/* 물타기 전략 */}
          <Box mt={8}>
            <HStack justify="space-between" mb={3}>
              <Text fontWeight="bold">{t('물타기 전략')}</Text>
              <HStack spacing={2}>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => setRoiTriggers([])}
                >
                  {t('초기화')}
                </Button>
                <Button size="xs" variant="outline" onClick={onBatchOpen}>
                  {t('일괄 설정')}
                </Button>
              </HStack>
            </HStack>

            <VStack align="stretch" spacing={3}>
              {/* 헤더 */}
              <Box
                display="grid"
                gridTemplateColumns="80px 1fr 1fr 32px"
                px={1}
                fontWeight="semibold"
                color="gray.600"
              >
                <Text textAlign="center">{t('회차')}</Text>
                <Text textAlign="center">ROI (%)</Text>
                <Text textAlign="center">{t('사이즈')}</Text>
                <Box />
              </Box>

              {roiTriggers.length === 0 ? (
                <Box textAlign="center" color="gray.500" py={4}>
                  {t('물타기 미사용 상태입니다.')}
                </Box>
              ) : (
                roiTriggers.map((item, index) => (
                  <Box
                    key={index}
                    display="grid"
                    gridTemplateColumns="80px 1fr 1fr 32px"
                    gap={2}
                    alignItems="center"
                  >
                    {/* 회차 */}
                    <Text textAlign="center">
                      {index + 1}
                      {t('회차')}
                    </Text>

                    {/* ROI 입력 */}
                    <Input
                      type="number"
                      min={1}
                      value={item.roi}
                      onChange={(e) => {
                        const updated = [...roiTriggers]
                        updated[index].roi = e.target.value
                        setRoiTriggers(updated)
                      }}
                      onBlur={() => {
                        if (!item.roi || Number(item.roi) < 1) {
                          const updated = [...roiTriggers]
                          updated[index].roi = '1'
                          setRoiTriggers(updated)
                        }
                      }}
                      placeholder="ROI"
                    />

                    {/* 사이즈 입력 */}
                    <Input
                      type="number"
                      min={1}
                      value={item.size}
                      onChange={(e) => {
                        const updated = [...roiTriggers]
                        updated[index].size = e.target.value
                        setRoiTriggers(updated)
                      }}
                      onBlur={() => {
                        if (!item.size || Number(item.size) < 1) {
                          const updated = [...roiTriggers]
                          updated[index].size = '1'
                          setRoiTriggers(updated)
                        }
                      }}
                      placeholder={t('사이즈') as string}
                    />

                    {/* 삭제 버튼 */}
                    <CloseButton onClick={() => removeRoiTrigger(index)} />
                  </Box>
                ))
              )}

              <Button
                leftIcon={<AddIcon />}
                w="100%"
                onClick={addRoiTrigger}
                colorScheme="purple"
                alignSelf="start"
              >
                {t('물타기 단계 추가')}
              </Button>
            </VStack>
          </Box>
        </Box>
      </Box>

      {/* 배치 설정 모달 */}
      <Modal isOpen={isBatchOpen} onClose={onBatchClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('물타기 전략 일괄 설정')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <HStack w="100%">
                <Text w="120px">{t('물타기 회수')}</Text>
                <Input
                  type="number"
                  value={batchCount}
                  onChange={(e) => setBatchCount(e.target.value)}
                />
              </HStack>
              <HStack w="100%">
                <Text w="120px">{t('시작 ROI (%)')}</Text>
                <Input
                  type="number"
                  value={batchRoi}
                  onChange={(e) => setBatchRoi(e.target.value)}
                />
              </HStack>
              <HStack w="100%">
                <Text w="120px">{t('사이즈')}</Text>
                <Input
                  type="number"
                  value={batchSize}
                  onChange={(e) => setBatchSize(e.target.value)}
                />
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" onClick={handleBatchApply}>
              {t('적용하기')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 시작/종료 확인 모달 */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {confirmAction === 'start'
              ? t('{{symbol}} 자동매매 시작 확인', { symbol: selectedSymbol })
              : t('{{symbol}} 자동매매 종료 확인', { symbol: selectedSymbol })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={1}>
              {t('정말로 {{symbol}} 자동매매를 {{verb}}하시겠습니까?', {
                symbol: selectedSymbol,
                verb: confirmAction === 'start' ? t('시작') : t('종료')
              })}
            </Text>

            {confirmAction === 'start' && (
              <Box bg="gray.50" borderRadius="md" p={4}>
                <Text fontWeight="bold" mb={2}>
                  {t('현재 설정 요약')}
                </Text>
                <Stack spacing={1}>
                  <HStack justify="space-between">
                    <Text>{t('종목')}</Text>
                    <Text>{selectedSymbol}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('레버리지')}</Text>
                    <Text>{t('{{n}}배', { n: leverage })}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('익절')}</Text>
                    <Text>{t('{{n}}%', { n: takeProfit })}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('손절')}</Text>
                    <Text>{useStopLoss ? `${stopLoss}%` : t('미사용')}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('사이즈')}</Text>
                    <Text>{size}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>{t('물타기 회수')}</Text>
                    <Text>
                      {t('{{n}}회', { n: roiTriggers.length.toString() })}
                    </Text>
                  </HStack>
                </Stack>
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onConfirmClose} variant="ghost" mr={3}>
              {t('취소')}
            </Button>
            <Button
              colorScheme={confirmAction === 'start' ? 'green' : 'red'}
              onClick={() => {
                handleToggleAutoTrade()
                onConfirmClose()
              }}
            >
              {t('확인')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default Trading
